import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import { contactAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const AdminContact = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    instagram: '',
    telegram: '',
    github: '',
    linkedin: '',
    twitter: ''
  });

  useEffect(() => {
    loadContact();
  }, []);

  const loadContact = async () => {
    try {
      const response = await contactAPI.get();
      setFormData({
        email: response.data.email || '',
        phone: response.data.phone || '',
        instagram: response.data.social_links?.instagram || '',
        telegram: response.data.social_links?.telegram || '',
        github: response.data.social_links?.github || '',
        linkedin: response.data.social_links?.linkedin || '',
        twitter: response.data.social_links?.twitter || ''
      });
    } catch (error) {
      console.error('Error loading contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const updateData = {
      email: formData.email,
      phone: formData.phone,
      social_links: {
        instagram: formData.instagram,
        telegram: formData.telegram,
        github: formData.github,
        linkedin: formData.linkedin,
        twitter: formData.twitter
      }
    };

    try {
      const response = await contactAPI.update(updateData);
      // Update form with the response data to ensure it shows the latest values
      setFormData({
        email: response.data.email || '',
        phone: response.data.phone || '',
        instagram: response.data.social_links?.instagram || '',
        telegram: response.data.social_links?.telegram || '',
        github: response.data.social_links?.github || '',
        linkedin: response.data.social_links?.linkedin || '',
        twitter: response.data.social_links?.twitter || ''
      });
      toast.success('Contact information updated successfully!');
    } catch (error) {
      toast.error('Failed to update contact information');
      console.error('Error updating contact:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  if (loading) {
    return (
      <div className="text-center py-20" data-testid="contact-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-contact-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-4xl font-bold font-['Syne'] mb-8" data-testid="contact-title">Edit Contact Information</h1>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6" data-testid="contact-form">
          <div>
            <label className="text-sm font-medium mb-2 block">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="hello@designer.com"
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 234 567 8900"
              data-testid="phone-input"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Social Media Links</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Instagram</label>
                <Input
                  value={formData.instagram}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  placeholder="@username or https://instagram.com/username"
                  data-testid="instagram-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Telegram</label>
                <Input
                  value={formData.telegram}
                  onChange={(e) => handleChange('telegram', e.target.value)}
                  placeholder="@username or https://t.me/username"
                  data-testid="telegram-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">GitHub</label>
                <Input
                  value={formData.github}
                  onChange={(e) => handleChange('github', e.target.value)}
                  placeholder="https://github.com/username"
                  data-testid="github-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">LinkedIn</label>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  data-testid="linkedin-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Twitter</label>
                <Input
                  value={formData.twitter}
                  onChange={(e) => handleChange('twitter', e.target.value)}
                  placeholder="https://twitter.com/username"
                  data-testid="twitter-input"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="save-contact-button"
          >
            {saving ? 'Saving...' : (
              <>
                <Save size={18} className="mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminContact;
